import React, { useMemo } from 'react';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { parseCurrency, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS SVG PREMIUM ---
const IconLoss = () => (
    <svg className="w-5 h-5 text-rose-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
    </svg>
);

const IconAlert = () => (
    <svg className="w-5 h-5 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

const IconUser = () => (
    <svg className="w-4 h-4 text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const IconBuilding = () => (
    <svg className="w-4 h-4 text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
);

/**
 * Componente modular de lista analítica para distribuciones de pérdidas por anulación
 * Reemplaza las tablas rígidas por renglones Flexbox fluidos y espaciosos con efectos hover
 */
const AnalyticsList = ({ title, icon, data, subLabelText = 'póliza caía' }) => (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-[0_2px_12px_-3px_rgba(0,0,0,0.02)] overflow-hidden text-left flex flex-col h-full transition-all duration-200 hover:shadow-md">
        {/* Cabecera superior con degradado suave */}
        <div className="p-4 px-5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 font-black text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-2 select-none">
            {icon} 
            <span>{title}</span>
        </div>
        
        {/* Cuerpo de la Lista */}
        <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto custom-scrollbar bg-white">
            {data.length === 0 ? (
                <div className="p-12 text-center text-slate-400 italic text-[11px] font-semibold select-none">
                    Sin anulaciones registradas en este período
                </div>
            ) : (
                data.map((row, idx) => (
                    <div 
                        key={idx} 
                        className="p-4 px-5 flex justify-between items-center hover:bg-slate-50/40 transition-colors duration-150 group"
                    >
                        {/* Izquierda: Nombre en negrita + Cantidad de cancelaciones abajo */}
                        <div className="flex flex-col text-left min-w-0 pr-4">
                            <span className="text-[12px] font-black uppercase text-slate-800 tracking-tight truncate group-hover:text-blue-900 transition-colors">
                                {row.nombre}
                            </span>
                            {row.cant !== undefined && (
                                <span className="text-[9px] text-slate-400 font-bold uppercase mt-0.5 tracking-wider select-none">
                                    {row.cant} {row.cant === 1 ? subLabelText : `${subLabelText.slice(0, -1)}as`}
                                </span>
                            )}
                        </div>
                        
                        {/* Derecha: Valores de pérdidas monetarias con signo negativo condicional */}
                        <div className="flex flex-col items-end shrink-0 select-none">
                            {row.ARS !== undefined && row.ARS > 0 && (
                                <span className="font-mono text-rose-600 font-black text-[11px] tracking-tight">
                                    {formatCurrencyUniversal(row.ARS, '- $')}
                                </span>
                            )}
                            {row.USD !== undefined && row.USD > 0 && (
                                <span className="font-mono text-blue-600 font-black text-[11px] tracking-tight mt-0.5">
                                    {formatCurrencyUniversal(row.USD, '- U$S')}
                                </span>
                            )}
                            {(!row.ARS && !row.USD) && <span className="text-slate-300 font-mono">-</span>}
                        </div>
                    </div>
                ))
            )}
        </div>
    </div>
);

/**
 * KpisAnulaciones - Dashboard analítico de pólizas canceladas, motivos de baja y primas perdidas.
 */
const KpisAnulaciones = ({ registros, isDbLoaded }) => {

    // --- PROCESAMIENTO ANALÍTICO DE PÉRDIDAS (BI ENGINE) ---
    const metrics = useMemo(() => {
        if (!isDbLoaded || !registros || registros.length === 0) {
            return {
                perdidaARS: 0,
                perdidaUSD: 0,
                cantidadAnuladas: 0,
                porMotivo: [],
                porResponsable: [],
                porCompania: []
            };
        }

        let perdidaARS = 0;
        let perdidaUSD = 0;
        let cantidadAnuladas = 0;

        const motivosMap = {};
        const responsablesMap = {};
        const companiasMap = {};

        registros.forEach(item => {
            // Consideramos anulados aquellos con fecha de anulación, primaAnulacion o estado final anulado
            const tieneFechaAnulacion = !!item.fechaAnulacion;
            
            // Aplicamos Math.abs al check de prima para validar correctamente registros con signo negativo
            const tienePrimaAnulacion = Math.abs(parseCurrency(item.primaAnulacion)) > 0;
            const esEstadoAnulado = String(item.estadoEmision || '').toUpperCase() === 'ANULADA';

            if (!tieneFechaAnulacion && !tienePrimaAnulacion && !esEstadoAnulado) return;

            cantidadAnuladas += 1;

            // Forzamos valor absoluto para asegurar suma acumulativa positiva de pérdidas
            const primaPerdida = Math.abs(parseCurrency(item.primaAnulacion));
            const monedaRaw = String(item.moneda || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

            if (esUSD) {
                perdidaUSD += primaPerdida;
            } else {
                perdidaARS += primaPerdida;
            }

            // Agrupación por Motivo de Anulación (Con valor absoluto para acumulados internos)
            const motivo = item.motivoAnulacion || 'SIN ESPECIFICAR';
            if (!motivosMap[motivo]) motivosMap[motivo] = { nombre: motivo, ARS: 0, USD: 0, cant: 0 };
            motivosMap[motivo].cant += 1;
            if (esUSD) motivosMap[motivo].USD += primaPerdida;
            else motivosMap[motivo].ARS += primaPerdida;

            // Agrupación por Responsable que gestionó la Anulación (Con valor absoluto para acumulados internos)
            const resp = item.quienGestionoAnulacion || item.quienGestiono || 'SIN ASIGNAR';
            if (!responsablesMap[resp]) responsablesMap[resp] = { nombre: resp, ARS: 0, USD: 0, cant: 0 };
            responsablesMap[resp].cant += 1;
            if (esUSD) responsablesMap[resp].USD += primaPerdida;
            else responsablesMap[resp].ARS += primaPerdida;

            // Agrupación por Compañía Aseguradora afectada (Con valor absoluto para acumulados internos)
            const cia = item.compania || 'SIN ESPECIFICAR';
            if (!companiasMap[cia]) companiasMap[cia] = { nombre: cia, ARS: 0, USD: 0, cant: 0 };
            companiasMap[cia].cant += 1;
            if (esUSD) companiasMap[cia].USD += primaPerdida;
            else companiasMap[cia].ARS += primaPerdida;
        });

        return {
            perdidaARS,
            perdidaUSD,
            cantidadAnuladas,
            porMotivo: Object.values(motivosMap).sort((a, b) => b.cant - a.cant),
            porResponsable: Object.values(responsablesMap).sort((a, b) => b.cant - a.cant),
            porCompania: Object.values(companiasMap).sort((a, b) => b.cant - a.cant)
        };
    }, [registros, isDbLoaded]);

    if (!isDbLoaded) {
        return (
            <div className="p-8 text-center text-slate-400 italic text-sm select-none">
                Cargando estadísticas de anulaciones...
            </div>
        );
    }

    if (metrics.cantidadAnuladas === 0) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-2xl border border-slate-200 select-none">
                ¡Excelente! No se registraron pólizas anuladas o canceladas en este período de fechas.
            </div>
        );
    }

    return (
        <div className="space-y-8 text-left animate-fade-in">
            
            {/* CARDS DE TOTALES DE PÉRDIDA POR ANULACIÓN */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                
                {/* PRIMA CAÍDA EN ARS */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Prima Anulada / Perdida (ARS)</p>
                        <h4 className="text-xl font-black text-rose-600 mt-2 font-mono tracking-tight">
                            {formatCurrencyUniversal(metrics.perdidaARS, '- $')}
                        </h4>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Pesos Argentinos caídos</p>
                    </div>
                    <span className="p-3 bg-red-50 text-red-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs select-none">
                        <IconLoss />
                    </span>
                </div>

                {/* PRIMA CAÍDA EN USD */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Prima Anulada / Perdida (USD)</p>
                        <h4 className="text-xl font-black text-blue-600 mt-2 font-mono tracking-tight">
                            {formatCurrencyUniversal(metrics.perdidaUSD, '- U$S')}
                        </h4>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Dólares caídos</p>
                    </div>
                    <span className="p-3 bg-blue-50 text-blue-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs select-none">
                        USD
                    </span>
                </div>

                {/* CANTIDAD DE ANULACIONES */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Contratos Anulados</p>
                        <h4 className="text-xl font-black text-slate-800 mt-2 font-mono">
                            {metrics.cantidadAnuladas}
                        </h4>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Pólizas canceladas en rango</p>
                    </div>
                    <span className="p-3 bg-slate-100 text-slate-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs select-none">
                        #
                    </span>
                </div>

            </div>

            {/* TABLAS DETALLADAS DE AUDITORÍA DE PÉRDIDAS EN LISTAS FLEXBOX */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
                
                {/* 1. MOTIVOS PRINCIPALES DE ANULACIÓN */}
                <AnalyticsList 
                    title="Ranking de Motivos de Anulación"
                    icon={<IconAlert />}
                    data={metrics.porMotivo}
                    subLabelText="póliza caía"
                />

                {/* 2. RESPONSABLE DE LA GESTIÓN DE ANULACIÓN */}
                <AnalyticsList 
                    title="Gestiones por Responsable"
                    icon={<IconUser />}
                    data={metrics.porResponsable}
                    subLabelText="trámite resuelto"
                />

                {/* 3. COMPAÑÍAS AFECTADAS */}
                <AnalyticsList 
                    title="Cartera Caída por Compañía"
                    icon={<IconBuilding />}
                    data={metrics.porCompania}
                    subLabelText="cancelación"
                />

            </div>

            {/* HOJA DE ESTILOS INLINE DE APOYO */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default KpisAnulaciones;